import React from 'react';

// Skeleton Loader Component
export const SkeletonLoader = ({ className = '', dark = false }) => {
  return (
    <div className={`${dark ? 'skeleton-dark' : 'skeleton'} rounded-lg ${className}`}></div>
  );
};

// Card Skeleton
export const CardSkeleton = ({ dark = false }) => {
  return (
    <div className={`p-6 rounded-xl ${dark ? 'bg-gray-800' : 'bg-white'} shadow-lg animate-fadeInUp`}>
      <div className="flex items-center space-x-4 mb-4">
        <SkeletonLoader className="w-12 h-12 rounded-full" dark={dark} />
        <div className="flex-1">
          <SkeletonLoader className="w-32 h-4 mb-2" dark={dark} />
          <SkeletonLoader className="w-24 h-3" dark={dark} />
        </div>
      </div>
      <SkeletonLoader className="w-full h-4 mb-2" dark={dark} />
      <SkeletonLoader className="w-3/4 h-4 mb-4" dark={dark} />
      <SkeletonLoader className="w-full h-8 rounded-lg" dark={dark} />
    </div>
  );
};

// Profile Skeleton
export const ProfileSkeleton = ({ dark = false }) => {
  return (
    <div className="animate-fadeInUp">
      <div className="text-center mb-8">
        <SkeletonLoader className="w-32 h-32 rounded-full mx-auto mb-4" dark={dark} />
        <SkeletonLoader className="w-48 h-6 mx-auto mb-2" dark={dark} />
        <SkeletonLoader className="w-32 h-4 mx-auto" dark={dark} />
      </div>
      
      <div className="space-y-4">
        <SkeletonLoader className="w-full h-4" dark={dark} />
        <SkeletonLoader className="w-5/6 h-4" dark={dark} />
        <SkeletonLoader className="w-4/5 h-4" dark={dark} />
      </div>
      
      <div className="flex flex-wrap gap-2 mt-6">
        {[...Array(5)].map((_, i) => (
          <SkeletonLoader key={i} className="w-20 h-8 rounded-full" dark={dark} />
        ))}
      </div>
    </div>
  );
};

// Message Skeleton
export const MessageSkeleton = ({ isOwn = false, dark = false }) => {
  return (
    <div className={`flex mb-4 animate-slideIn${isOwn ? 'Right' : 'Left'}`}>
      {!isOwn && <SkeletonLoader className="w-8 h-8 rounded-full mr-2" dark={dark} />}
      <div className={`flex-1 ${isOwn ? 'text-right' : ''}`}>
        <SkeletonLoader className={`${isOwn ? 'w-48 ml-auto' : 'w-48'} h-4 mb-1`} dark={dark} />
        <SkeletonLoader className={`${isOwn ? 'w-32 ml-auto' : 'w-32'} h-3`} dark={dark} />
      </div>
      {isOwn && <SkeletonLoader className="w-8 h-8 rounded-full ml-2" dark={dark} />}
    </div>
  );
};

// Typing Indicator
export const TypingIndicator = ({ name = "Someone" }) => {
  return (
    <div className="flex items-center space-x-2 text-gray-500 animate-fadeInUp">
      <div className="flex space-x-1">
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
      </div>
      <span className="text-sm">{name} is typing...</span>
    </div>
  );
};

// Loading Spinner with Text
export const LoadingSpinner = ({ text = "Loading...", size = "md", color = "rose" }) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-8 h-8",
    lg: "w-12 h-12",
    xl: "w-16 h-16"
  };

  const colorClasses = {
    rose: "border-rose-500",
    blue: "border-blue-500",
    green: "border-green-500",
    purple: "border-purple-500"
  };

  return (
    <div className="flex flex-col items-center justify-center space-y-4 animate-fadeInUp">
      <div className={`animate-spin rounded-full ${sizeClasses[size]} border-2 ${colorClasses[color]} border-t-transparent`}></div>
      {text && <p className="text-gray-600 animate-pulse">{text}</p>}
    </div>
  );
};

// Progress Bar
export const ProgressBar = ({ progress = 0, label = "", animated = true, color = "rose" }) => {
  const colorClasses = {
    rose: "bg-gradient-to-r from-rose-500 to-pink-500",
    blue: "bg-gradient-to-r from-blue-500 to-cyan-500",
    green: "bg-gradient-to-r from-green-500 to-emerald-500",
    purple: "bg-gradient-to-r from-purple-500 to-violet-500"
  };

  return (
    <div className="w-full animate-fadeInUp">
      {label && <div className="text-sm font-medium text-gray-700 mb-2">{label}</div>}
      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
        <div 
          className={`h-3 ${colorClasses[color]} rounded-full transition-all duration-700 ease-out ${animated ? 'animate-pulse' : ''}`}
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        ></div>
      </div>
      <div className="text-xs text-gray-500 mt-1 text-right">{Math.round(progress)}%</div>
    </div>
  );
};

// Success Animation
export const SuccessAnimation = ({ message = "Success!", onComplete }) => {
  React.useEffect(() => {
    if (onComplete) {
      const timer = setTimeout(onComplete, 2000);
      return () => clearTimeout(timer);
    }
  }, [onComplete]);

  return (
    <div className="flex flex-col items-center justify-center space-y-4 animate-scaleIn">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
        <svg className="w-8 h-8 text-green-500 animate-checkmark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <p className="text-green-600 font-semibold animate-fadeInUp">{message}</p>
    </div>
  );
};

// Error Animation
export const ErrorAnimation = ({ message = "Something went wrong", onRetry }) => {
  return (
    <div className="flex flex-col items-center justify-center space-y-4 animate-shake">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <p className="text-red-600 font-semibold text-center">{message}</p>
      {onRetry && (
        <button 
          onClick={onRetry}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
        >
          Try Again
        </button>
      )}
    </div>
  );
};

export default {
  SkeletonLoader,
  CardSkeleton,
  ProfileSkeleton,
  MessageSkeleton,
  TypingIndicator,
  LoadingSpinner,
  ProgressBar,
  SuccessAnimation,
  ErrorAnimation
};